/**
 * Token utility functions for managing refresh tokens
 */

import { GET } from "../components/constants/request-type";
import { service_api } from "../components/constants/url";
import { MakeRequestAsync } from "../components/functions/axios";
import { decryptSingleData } from "../components/functions/cryptojs";
import { getStoredRefreshToken, getStoredUserData, storeUserData } from "./authStorageUtils";
import { createLogger } from "./logger";

const logger = createLogger("TokenUtils");

/**
 * Fetch fresh user data including refresh_token from the server
 * @param {string} userId - User ID
 * @param {string} currentToken - Current refresh token (if available)
 * @returns {Promise<Object|null>} - Fresh user data or null if failed
 */
export const fetchFreshUserData = async (userId, currentToken = null) => {
  if (!userId) {
    logger.warn("🚨 fetchFreshUserData: No userId provided");
    return null;
  }

  try {
    const request_details = {
      type: GET,
      url: service_api,
      route: `user/get-user/${userId}`,
      data: null,
      token: currentToken
    };

    logger.log("🔄 Fetching fresh user data:", {
      userId,
      hasCurrentToken: !!currentToken,
      route: request_details.route
    });

    const response = await MakeRequestAsync(request_details);
    const decryptedData = decryptSingleData(response.data.user);
    
    logger.log("✅ Fresh user data fetched:", {
      hasRefreshToken: !!decryptedData?.refresh_token,
      tokenLength: decryptedData?.refresh_token?.length,
      userId: decryptedData?._id,
      userDataKeys: decryptedData ? Object.keys(decryptedData) : []
    });

    return decryptedData;
  } catch (err) {
    logger.error("❌ Failed to fetch fresh user data:", {
      error: err,
      message: err?.response?.data?.message,
      status: err?.response?.status,
      userId
    });
    return null;
  }
};

/**
 * Extract refresh token from user data, with fallback to fetch fresh data
 * @param {Object} userData - Current user data from Redux store
 * @param {Function} updateUserData - Function to update user data in Redux store
 * @returns {Promise<string|null>} - Refresh token or null if not available
 */
export const getRefreshToken = async (userData, updateUserData = null) => {
  logger.log("🔍 getRefreshToken called with userData:", {
    hasUserData: !!userData,
    userId: userData?._id,
    hasRefreshToken: !!userData?.refresh_token,
    refreshTokenValue: userData?.refresh_token,
    refreshTokenType: typeof userData?.refresh_token,
    userDataKeys: userData ? Object.keys(userData) : [],
    userDataSample: userData
  });

  // If refresh token exists in Redux, return it
  if (userData?.refresh_token) {
    logger.log("✅ Refresh token found in Redux userData:", {
      tokenValue: userData.refresh_token,
      tokenLength: userData.refresh_token?.length,
      tokenStart: userData.refresh_token?.substring(0, 20),
      tokenType: typeof userData.refresh_token,
      hasBearer: userData.refresh_token?.startsWith('Bearer ')
    });
    return userData.refresh_token;
  }

  // Check localStorage for stored token as fallback
  const storedToken = getStoredRefreshToken();
  if (storedToken) {
    logger.log("✅ Refresh token found in localStorage, using as fallback:", {
      tokenValue: storedToken,
      tokenLength: storedToken?.length,
      tokenStart: storedToken?.substring(0, 20),
      tokenType: typeof storedToken,
      hasBearer: storedToken?.startsWith('Bearer ')
    });
    
    // Try to get full user data from localStorage and update Redux if available
    const storedUserData = getStoredUserData();
    if (storedUserData && updateUserData && typeof updateUserData === 'function') {
      logger.log("🔄 Updating Redux with stored user data from localStorage");
      updateUserData(storedUserData);
    }
    
    return storedToken;
  }

  // If no user ID, can't fetch token
  if (!userData?._id) {
    logger.warn("🚨 getRefreshToken: No user ID available and no stored token");
    return null;
  }

  logger.log("🔄 Refresh token missing in Redux store, fetching fresh user data from server...");
  
  // Attempt to fetch fresh user data
  const freshUserData = await fetchFreshUserData(userData._id, userData?.refresh_token);
  
  if (freshUserData?.refresh_token) {
    logger.log("✅ Fresh refresh token retrieved from server:", {
      hasToken: !!freshUserData.refresh_token,
      tokenLength: freshUserData.refresh_token?.length
    });
    
    // Update Redux store if update function is provided
    if (updateUserData && typeof updateUserData === 'function') {
      logger.log("🔄 Updating Redux store with fresh user data");
      updateUserData(freshUserData);
    }
    
    // Store fresh user data in localStorage for persistence
    storeUserData(freshUserData);
    
    return freshUserData.refresh_token;
  }

  logger.warn("⚠️ Could not retrieve refresh token from server either");
  return null;
};

/**
 * Validate if refresh token exists and is properly formatted
 * @param {string} token - Token to validate
 * @returns {boolean} - Whether token is valid
 */
export const validateRefreshToken = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Basic JWT format validation (header.payload.signature)
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  
  // Check minimum length (very basic check)
  if (token.length < 100) {
    return false;
  }
  
  return true;
};

/**
 * Check if user is fully authenticated (has ID and valid refresh token)
 * @param {Object} userData - User data to check
 * @returns {boolean} - Whether user is fully authenticated
 */
export const isUserAuthenticated = (userData) => {
  return !!(userData?._id && validateRefreshToken(userData?.refresh_token));
};